import axios, { AxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// @TODO rule `import/no-webpack-loader-syntax` should be added to eslinconfig
// eslint-disable-next-line import/no-webpack-loader-syntax
import supportEmailCompile from '!!ejs-compiled-loader!./../../ui/templates/support.mail.ejs';

import { SubmitFormValues } from '../../interfaces';
import { ApiErrorReasons, AppConstants, FormType, InputsLabel } from '../enums';
import { wrapTags } from '../helpers';

const DEFAULT_ERROR_MESSAGE =
  'Le traitement de votre demande n’a pas abouti, veuillez réessayer ultérieurement';

export const useSubmitFormData = () => {
  const navigate = useNavigate();

  const submitErrorHandler = useCallback((err: AxiosError<{ message: string }>) => {
    let message;
    if (err.response) {
      message = `Code d’erreur ${err.response.status}`;
      if (err.response.data.message) {
        const msg = err.response.data.message as keyof typeof ApiErrorReasons;
        const reason: string = ApiErrorReasons[msg] || DEFAULT_ERROR_MESSAGE;
        message = `${message} : ${reason}`;
      }
    } else {
      message = DEFAULT_ERROR_MESSAGE;
    }

    return { [FORM_ERROR]: message };
  }, []);

  const submitSuccessHandler = useCallback(() => {
    navigate('/confirmation', { replace: true });
  }, [navigate]);

  const submitDataForm = useCallback(
    async ({
      birthcountry,
      birthdate,
      birthplace,
      email,
      encounteredProblem,
      errorCode,
      errorId,
      fileUpload,
      firstName,
      firstNameDisabled,
      formName,
      formType,
      fraudSurveyOrigin,
      identityProvider,
      lastName,
      lastNameDisabled,
      phone,
      platform,
      serviceProvider,
      sessionId,
      socialSecurity,
      textareaDescription: description,
    }: SubmitFormValues) => {
      const isSecuritySupport = formType === FormType.SECURITY || formType === FormType.FRAUD;
      const formData = new FormData();
      formData.append('formname', formName);
      formData.append('mailuser', email || 'NC');
      formData.append('mailsupport', isSecuritySupport ? 'security' : 'usagers');

      if (errorCode) {
        formData.append('errorCode', errorCode);
      }

      if (sessionId) {
        formData.append('sessionId', sessionId);
      }

      if (encounteredProblem) {
        formData.append('encounteredProblem', encounteredProblem);
      }

      if (fileUpload !== undefined && fileUpload.length > 0) {
        formData.append('file', fileUpload[0], fileUpload[0].name);
      }

      if (isSecuritySupport) {
        formData.append('phoneNumer', phone || 'NC');
      }

      formData.append(
        'message',
        supportEmailCompile({
          birthcountry,
          birthdate,
          birthplace,
          description,
          email,
          encounteredProblem,
          errorCode,
          errorId,
          firstName: (firstNameDisabled && InputsLabel.FIRSTNAME_DISABLED) || firstName,
          fraudSurveyOrigin,
          hasSocialSecurity: socialSecurity,
          identityProvider,
          lastName: (lastNameDisabled && InputsLabel.LASTNAME_DISABLED) || lastName,
          phone,
          serviceProvider,
          sessionId,
          tags: wrapTags({
            FI: identityProvider,
            FS: serviceProvider,
            NSS: socialSecurity,
            plateforme: platform,
          }),
        }),
      );

      try {
        const url = `${AppConstants.BASENAME}/api/sendMail.php`;
        const headers = {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'multipart/form-data',
        };
        await axios.post(url, formData, { headers });
        return submitSuccessHandler();
      } catch (err) {
        return submitErrorHandler(err as AxiosError);
      }
    },
    [submitErrorHandler, submitSuccessHandler],
  );

  return { submitDataForm };
};
